import React, {useEffect, useRef, useState} from 'react';
import {ActivityIndicator, FlatList} from 'react-native';
import {SessionProgramDataTable} from 'src/modules/session/components';
import NoResults from 'src/common-components/noResults';
import {compose} from 'recompose';
import {Q} from '@nozbe/watermelondb';
import {useDatabase} from '@nozbe/watermelondb/react';
import {withDatabase} from '@nozbe/watermelondb/react';
import {withObservables} from '@nozbe/watermelondb/react';
import {oneOf} from '@nozbe/watermelondb/QueryDescription';
import _ from 'lodash';
import {SYNC_SERVER} from 'react-native-dotenv';
import {Program, Set, SessionProgram} from 'src/models';
import AsyncStorage from '@react-native-async-storage/async-storage';

const SessionSummaryDataTable = ({
  session,
  showGraphs = true,
  programs,
  showProgramMenu = false,
}: any) => {
  const [reorderedPrograms, setReorderedPrograms] = useState(programs);
  const [programAnalysis, setAnalysis] = useState([]);

  const [recentSets, setRecentSets] = useState([]);
  const [recentEvents, setEvents] = useState([]);

  const [isLoading, setIsLoading] = useState(false);

  const reorderedProgramsRef = useRef(reorderedPrograms);
  const database = useDatabase();

  useEffect(() => {
    setReorderedPrograms(programs);
  }, [programs]);

  useEffect(() => {
    if (programs.length === 0) {
      const fetchPrograms = async () => {
        setIsLoading(true);
        try {
          const response = await fetch(
            `${SYNC_SERVER}/fetch-programs?sessionId=${session.id}`,
            {
              method: 'GET',
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: `Bearer ${await AsyncStorage.getItem(
                  'accessToken',
                )}`,
              },
            },
          );
          if (response.status === 200) {
            const values = await response.json();
            const sessionProgramIds = values?.programs.map(
              item => item.program_id,
            );
            const sessionPrograms = await database
              .get('programs')
              .query(Q.where('id', Q.oneOf(sessionProgramIds)));

            if (sessionPrograms) {
              reorderedProgramsRef.current = sessionPrograms;
            }

            setReorderedPrograms(sessionPrograms);
            setAnalysis(values?.programsAnalysis);
            setRecentSets(values?.recentSets);
            setEvents(values?.setEvents);
          }
        } catch (e) {
          console.log('fetch error:', e);
        } finally {
          setIsLoading(false);
        }
      };

      fetchPrograms();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.id]);

  const reorderPrograms = prevProgram => {
    setReorderedPrograms(prevOrderedPrograms => {
      let newArr = prevOrderedPrograms.filter(
        program => program.id !== prevProgram.id,
      );
      newArr.unshift(prevProgram);
      return _.uniq(newArr, (program: any) => program.id);
    });
  };

  return (
    <>
      {isLoading ? (
        <>
          <ActivityIndicator size={'large'} />
        </>
      ) : (
        <>
          {reorderedProgramsRef?.current?.length ? (
            <FlatList
              data={reorderedProgramsRef.current}
              keyExtractor={item => `session-program-data-key-${item?.id}`}
              renderItem={({item, index}: any) => (
                <SessionProgramDataTable
                  program={item}
                  session={session}
                  showGraphs={showGraphs}
                  reorderPrograms={reorderPrograms}
                  index={index}
                  showProgramMenu={showProgramMenu}
                  useObservables={programs?.length > 0 ? true : false}
                  analysis={
                    programAnalysis[item?.id] ? programAnalysis[item?.id] : null
                  }
                  analyzedSets={recentSets}
                  analyzedEvents={recentEvents}
                />
              )}
            />
          ) : (
            <NoResults
              iconName={'file-outline'}
              secondaryMessage={
                'No programs have been enabled for this session.'
              }
            />
          )}
        </>
      )}
    </>
  );
};

export default React.memo(
  compose(
    withDatabase,
    withObservables(
      ['filters'],
      ({session, filters, database, showAllPrograms = false}: any) => {
        const queries = [];
        if (filters?.types && filters.types.length) {
          queries.push(Q.where('type', oneOf(filters.types)));
        }
        if (filters?.programs && filters.programs.length) {
          queries.push(Q.where('id', oneOf(filters.programs)));
        }
        return {
          programs: showAllPrograms
            ? database
                .get(Program.table)
                .query(
                  Q.on(
                    SessionProgram.table,
                    Q.and(
                      Q.where('session_id', session?.id || null),
                      Q.where('enabled', true),
                    ),
                  ),
                  Q.where('deleted_at', null),
                  Q.where('state', Q.notEq('archived')),
                  ...queries,
                )
            : database
                .get(Program.table)
                .query(
                  ...queries,
                  Q.on(
                    Set.table,
                    Q.and(
                      Q.where('session_id', session?.id || null),
                      Q.where('deleted_at', null),
                    ),
                  ),
                  Q.where('deleted_at', null),
                  Q.where('state', Q.notEq('archived')),
                ),
        };
      },
    ),
    // withObservables(['programs'], ({sets, programs}: any) => {
    //   const sortedPrograms = programs.sort((a, b) => {
    //     const timestampA =
    //       sets.find(set => set.programId === a.id)?.updatedAt || 0;
    //     const timestampB =
    //       sets.find(set => set.programId === b.id)?.updatedAt || 0;
    //     return timestampB - timestampA;
    //   });
    //   return {
    //     sortedPrograms: of(sortedPrograms),
    //   };
    // }),
  )(SessionSummaryDataTable),
);
